/* eslint-disable react/display-name */
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import fetcher from '../lib/fetcher';
import { useNotificationStore } from '../store/notifications';
import Cookies from 'js-cookie';
import { NextPage } from 'next';

export type TokenProviderProps = {
  children: ReactNode;
};

export type TokenContextProps = {
  token?: string;
  fetchToken: () => void;
};

const defaultProps: TokenContextProps = {
  token: '',
  fetchToken: () => {}
};

export const TokenContext = React.createContext<TokenContextProps>(defaultProps);

export default function TokenProvider({ children }: TokenProviderProps) {
  const [token, setToken] = useState<string | undefined>(undefined);
  const addNotification = useNotificationStore(state => state.addNotification);
  const { t } = useTranslation('common');

  const fetchToken = useCallback(async () => {
    try {
      const { data } = await fetcher<{ token: string }>('/token');

      // Set cookie and let it expire after one hour
      Cookies.set('token', data.token, { expires: 1 / 24 });
      setToken(data.token);
      return data.token;
    } catch (error) {
      addNotification({ text: t('response.general.error') });
    }
  }, [addNotification, t]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  return <TokenContext.Provider value={{ token, fetchToken }}>{children}</TokenContext.Provider>;
}

export function withToken<T>(Component: NextPage<T>) {
  return ({ children, ...props }: any) => (
    <TokenProvider>
      <Component {...props}>{children}</Component>
    </TokenProvider>
  );
}
